import type { CartItem } from '#types/cart'
import type { Responsive } from '#types/common'
import type { FilterByContent } from '#types/components/cms/filter-by'
import type { InlineGridItem, InlineGridSection } from '#types/components/product/list'
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router'
import ds from '#root/design-system'

/**
 * Converts inline-grid eSpot items array into an object with keys as positions in the grid.
 * Each column covered represents one index, so if an eSpot covers 4 columns this takes up 4 indices
 * and future eSpots based on position will need to account for that.
 * If variant is full width CMS user must use relative positioning with left alignment.
 * Each value is a "sections" object with a name, id, some positioning information and an items array.
 * @param items An array of inline grid items.
 * @param cols The number of columns for each breakpoint.
 * @param productsLength The total number of products.
 * @returns An array of inline grid sections.
 * @category Utils
 */
export const getResponsivePosition = (
  items: InlineGridItem[],
  cols: Responsive<number>,
  productsLength: number
): InlineGridSection[] => {
  const brs = Object.keys(ds.breakpoints) as (keyof Responsive)[]
  const offset: Record<string, number> = { sm: 0, md: 0, lg: 0 }

  return Array.from(items, (item) => ({
    name: `inline-grid-${item.id}`,
    items: [
      {
        ...item.component,
        ...(item.promotionTracking && { promotionTracking: item.promotionTracking }),
        ...(item.parallax && { parallax: item.parallax })
      }
    ],
    id: item.id,
    naturalHeight: item.naturalHeight,
    ...brs.reduce(({ column, display, position, row, width, height, size }, breakpoint) => {
      const columns = getValueForBreakpoint(breakpoint, cols) || 0
      const responsivePosition = getValueForBreakpoint(breakpoint, item?.responsivePosition)
      const [pos, colSpan, rowSpan] = (responsivePosition?.[`col${columns}`] || '0/1/1').split('/')
      const _position = +pos
      const _colSpan = +(colSpan || '1')
      const _rowSpan = +(rowSpan || '1')

      row[breakpoint] = `${Math.ceil(_position / columns) || 0} / span ${_rowSpan}`
      column[breakpoint] = `${_position % columns || columns} / span ${_colSpan}`
      position[breakpoint] = _position
      width[breakpoint] = _colSpan
      height[breakpoint] = _rowSpan
      size[breakpoint] = _colSpan * _rowSpan

      const totalRows = Math.ceil((productsLength + offset[breakpoint]) / columns)
      const isOutOfBounds = !columns || Math.ceil(_position / columns) > totalRows
      const isVisible = !isOutOfBounds && !item.hideOnBreakpoint?.[breakpoint]

      display[breakpoint] = isVisible ? 'block' : 'none'
      if (isVisible) offset[breakpoint] += size[breakpoint]

      return { column, display, position, row, width, height, size }
    }, {
      column: {},
      display: {},
      position: {},
      row: {},
      width: {},
      height: {},
      size: {}
    })
  } as InlineGridSection))
}

/**
 * Resolves the eSpot items to render inside an in-grid section. Expands `VfCanvasFilterBy`
 * components against the route + attributes, applies an optional `filterFn`, strips inherited
 * side paddings / max widths from each section, and assigns each item a responsive grid position.
 * @param contents - The raw inline-grid items from CMS.
 * @param products - Number of product cards in the grid (used by the position resolver).
 * @param cols - Responsive number of grid columns per breakpoint.
 * @param ctx - Routing / filtering context.
 * @param ctx.route - The current Vue Router route (used by `VfCanvasFilterBy`).
 * @param ctx.attributes - Optional attribute filters applied to filter-by sections.
 * @param ctx.cartItems - Optional current cart items, used by `VfCanvasFilterBy`'s `byCart` filtering.
 * @param filterFn - Optional predicate to drop items before positioning.
 * @returns The positioned, filtered list of in-grid items ready to render.
 * @category Utils
 */
export const getFilteredEspots = (
  contents: InlineGridItem[],
  products: number = 0,
  cols: Responsive<number>,
  { route, attributes, cartItems }: {
    route: RouteLocationNormalizedLoadedGeneric
    attributes?: Record<string, string>
    cartItems?: CartItem[]
  },
  filterFn: (section: InlineGridItem) => boolean = () => true
) => {
  const items = contents
    .flatMap((component) => component.type === 'VfCanvasFilterBy'
      ? filterContentBy(component as FilterByContent, { route, attributes, cartItems }) as InlineGridItem[]
      : [component])
    .filter(filterFn)
    .map((section) => {
      if (section.component) {
        section.component.componentSidePaddings = undefined
        section.component.roundedCorners = {
          sm: 'disabled',
          lg: 'enabled'
        }
        section.component.roundedCornerPreset = 'small'
        section.component.componentMaxWidth = undefined
      }
      return section
    })

  return getResponsivePosition(items, cols, products)
}

/**
 * Get inline grid eSpot style classes.
 *
 * In-grid eSpots should not be higher than product card height.
 * To achieve this we need to add few classes to wrapper.
 * The only exclusion is full width eSpots which could have any height so classes should not be added.
 *
 * @param gridCols - Responsive grid column configuration
 * @param espotSizes - Responsive eSpot size configuration
 * @returns CSS class string for the eSpot wrapper
 */
export const getInGridEspotStyle = (gridCols: Responsive<number>, espotSizes: Responsive<number>) => {
  // List of classes to avoid Unocss purging
  const styleClasses = {
    sm: getValueForBreakpoint('sm', gridCols) !== getValueForBreakpoint('sm', espotSizes) && '~sm:([&_img]:(h-0 min-h-full))',
    md: getValueForBreakpoint('md', gridCols) !== getValueForBreakpoint('md', espotSizes) && '~md:([&_img]:(h-0 min-h-full))',
    lg: getValueForBreakpoint('lg', gridCols) !== getValueForBreakpoint('lg', espotSizes) && '~lg:([&_img]:(h-0 min-h-full))',
  }

  return Object.values(styleClasses).filter(Boolean).join(' ')
}
